// Analytics-Related Functionality

// Master switch; Set to false to immediately stop analytics
let enabled        = window.arff?.analyticsEnabled || false;

// If false, analytics collection occurs but without user ids (clientDeviceId values)
let shouldIdentify = window.arff?.analyticsIdentify || false;

// If true, get helpful debugging messages (dev only)
let shouldLog      = window.arff?.analyticsLog || false;



// ---------- BEGIN: WebRemote Server-Based Analytics Implementation ----------

// Key configuration constants
const serverUrl = '/api/v1/analytics';
const batchSize = 5;
const idleTimeMs = 1000;

// Core "state" variables
let currentIdentity = undefined;
let currentTraits = {};
let currentGroup = undefined;

let consentSettings = {}; // Keys: ad_storage, analytics_storage, personalization_storage, functionality_storage, security_storage
const eventQueue = [];
let isSending = false;
let timeout = null;



// Automatically send events on page transitions
// - For traditional page unloads
window.addEventListener('beforeunload', () => flushEvents());
// - For single-page apps with history navigation
window.addEventListener('popstate', () => flushEvents());



// Add event to the queue and either flush if queue is full or schedule a flush
// NOTE: Obeys consent settings, so if analytics_storage is 'denied', events are not queued/sent!
function pushEvent(event) {
	// Better safe than sorry
	if ( ! enabled ) { return; }

	// Obey consent settings (analytics_storage in particular)
	if ( consentSettings.analytics_storage !== 'granted' ) {
		return;
	}

	// Add context to event
	// - group (partner) and timestamp always
	// - identity/traits if shouldIdentify is true
	const eventWithContext = {
		...event,
		group: currentGroup,
		timestamp: new Date().toISOString()
	};
	if ( shouldIdentify && currentIdentity ) {
		if ( currentIdentity ) {
			eventWithContext.userId = currentIdentity;
		}
		if ( currentTraits ) {
			eventWithContext.traits = currentTraits;
		}
	}

	// Queue the event (will eventually be sent to server)
	eventQueue.push(eventWithContext);

	// If queue is full, flush immediately; otherwise, schedule a flush since we know there's at least one event
	if (eventQueue.length >= batchSize) {
		flushEvents();
	} else {
		scheduleFlush();
	}
}

// Schedule event flushing based on idle time
function scheduleFlush() {
	if (timeout) {
		clearTimeout(timeout);
	}

	timeout = setTimeout(() => flushEvents(), idleTimeMs);
}

// Flush events when idle or page transition happens
async function flushEvents() {
	if (isSending || eventQueue.length === 0) return;

	isSending = true;

	// Send the events in batches
	const eventsToSend = eventQueue.splice(0, batchSize);

	try {
	  await sendToServer(eventsToSend);
	} catch (error) {
		console.error("Error sending events:", error);
	}

	isSending = false;

	// If there are still events, schedule another flush
	if (eventQueue.length > 0) {
		scheduleFlush();
	}
}

// Send the events to the server
async function sendToServer(events) {
	const payload = {
		events,
		timestamp: new Date().toISOString(),
	};

	try {
		await fetch(serverUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		if ( shouldLog ) {
			console.log('Events successfully sent');
		}
	} catch (error) {
		console.error('Error sending events to server:', error);
		// @TODO: Could add retry logic can be added here
	}
}

// ---------- END: WebRemote Server-Based Analytics Implementation ----------

/**
 * Control what consent, if any, has been given for analytics collection
 * @param {boolean} isUpdate                    Are these settings from a user update (true) or initial/default settings (false)
 * @param {Object} newConsentSettings           An object with consent settings; e.g., { ad_storage: 'granted'|'denied', analytics_storage: 'granted'|'denied', personalization_storage: 'granted'|'denied' }
 * @return {undefined}
 */
function setConsentSettings(isUpdate, newConsentSettings) {
	if ( typeof isUpdate !== 'boolean' ) {
		console.log('analytics: setConsentSettings: Invalid isUpdate parameter; must be boolean');
		return;
	}

	if ( typeof newConsentSettings !== 'object' ) {
		console.log('analytics: setConsentSettings: Invalid consentSettings parameter; must be an object');
		return;
	}

	if ( typeof consentSettings !== 'object' ) {
		console.log('analytics: setConsentSettings: Invalid consentSettings parameter; must be an object');
		return;
	}

	// Store consent settings in localStorage for persistence
	localStorage.setItem('consentMode', JSON.stringify(newConsentSettings));

	if ( shouldLog ) {
		console.log(`analytics: setConsentMode: consentSettings=${JSON.stringify(consentSettings)}`);
	}

	// Update our internal consent settings
	consentSettings = newConsentSettings;

	// Let the server know about the updated consent settings; WebRemote server-based implementation
	if ( isUpdate ) {
		pushEvent({
			command: 'consent',
			settings: consentSettings
		});
	}
}

/**
 * Control whether logging is on or off
 * @param  {Boolean} shouldLog                   Set to true for logs; false for no logs
 * @return {undefined}
 */
function debug(pShouldLog) {
	shouldLog = pShouldLog;
}

/**
 * Link users and their actions to a recognizable userId and traits
 * @param  {Object}  params                      Parameters object
 * @param  {String}  params.userId               The current clientDeviceId value; required
 * @param  {Array}   params.traits               A dictionary of traits known about the user; optional
 * @param  {Integer} params.callback             A function executed after a timeout of 300 ms, giving the browser time to make outbound requests first; optional
 * @return {undefined}
 */
function identify(params) {
	if ( ! enabled ) { return; }
	if ( ! shouldIdentify ) { return; }

	let { userId, traits, callback } = params;

	if ( ! userId ) {
		console.log('analytics: identify: Missing userId parameter')
		return;
	}

	if ( shouldLog ) {
		const paramsToLog = {};
		['userId', 'traits'].forEach((key) => {
			paramsToLog[key] = params[key];
		});
		console.log(`analytics: identify: ${JSON.stringify(paramsToLog)}`);
	}

	// Update our internal state
	currentIdentity = userId || undefined;
	currentTraits = traits || {};

	// WebRemote server-based implementation
	pushEvent({
		command: 'config',
		parameter_name: 'user_id',
		parameter_value: userId
	});
}

/**
 * Associate an identified user with some kind of group (e.g., syndication partner)
 * @param  {Object}  params                      Parameters object
 * @param  {String}  params.groupId              The Group ID to associate with the current user; required
 * @param  {Integer} params.traits               A dictionary of traits for the group; optional
 * @param  {Integer} params.callback             A function executed after a timeout of 300 ms, giving the browser time to make outbound requests first; optional
 * @return {undefined}
 */
function group(params) {
	if ( ! enabled ) { return; }

	let { groupId, traits, callback } = params;

	if ( ! groupId ) {
		console.log('analytics: group: Missing groupId parameter')
		return;
	}

	if ( shouldLog ) {
		const paramsToLog = {};
		['groupId', 'traits'].forEach((key) => {
			paramsToLog[key] = params[key];
		});
		console.log(`analytics: group: ${JSON.stringify(paramsToLog)}`);
	}

	// Update our internal state
	currentGroup = groupId || undefined;

	// WebRemote server-based implementation
	pushEvent({
		command: 'set',
		parameter_name: 'partner',
		parameter_value: params?.groupId,
		traits: traits || {}
	});
}

/**
 * Record page views within app, along with extra information about the page viewed by the user. Note: You must call this method at least once per page load. You can call it more than once if needed, for example, on virtual page changes in a single page app
 * @param  {Object}  params                      Parameters object
 * @param  {String}  params.pageName             The name of the page; required
 * @param {String}   params.path                 The path of the page; required
 * @param  {Integer} params.properties           A dictionary of properties of the page; optional; Note: url, title, referrer, path are automatically included
 * @param  {Integer} params.callback             A function executed after a timeout of 300 ms, giving the browser time to make outbound requests first; optional
 * @return {undefined}
 */
function page(params) {
	if ( ! enabled ) { return; }

	let { pageName, path, properties, callback } = params;

	if ( ! pageName ) {
		console.log('analytics: page: Missing pageName parameter')
		return;
	}

	if ( ! path ) {
		console.log('analytics: page: Missing path parameter')
		return;
	}

	if ( shouldLog ) {
		const paramsToLog = {};
		['pageName', 'path', 'properties'].forEach((key) => {
			paramsToLog[key] = params[key];
		});
		console.log(`analytics: page: ${JSON.stringify(paramsToLog)}`);
	}

	// WebRemote server-based implementation
	pushEvent({
		command: 'event',
		eventName: 'page_view',
		properties: {
			page_title: pageName,
			page_location: window.location.href,
			page_path: path,
			...properties
		}
	});
}

/**
 * Record actions users perform
 * @param  {Object}  params                      Parameters object
 * @param  {String}  params.eventName            Concise description of the event that took place; r.g., 'stb_button'; required
 * @param  {String}  params.properties           A dictionary of properties for the event; optional
 * @param  {Integer} params.callback             A function executed after a timeout of 300 ms, giving the browser time to make outbound requests first; optional
 * @return {undefined}
 */
function track(params) {
	if ( ! enabled ) { return; }

	let { eventName, properties, callback } = params;

	if ( ! eventName ) {
		console.log('analytics: track: Missing eventName parameter')
		return;
	}

	if ( shouldLog ) {
		const paramsToLog = {};
		['eventName', 'properties'].forEach((key) => {
			paramsToLog[key] = params[key];
		});
		console.log(`analytics: track: ${JSON.stringify(paramsToLog)}`);
	}

	// WebRemote server-based implementation
	pushEvent({
		command: 'track',
		eventName: eventName,
		properties: properties || {}
	});
}

// Exported API
const Analytics = {
	setConsentSettings : setConsentSettings,
	debug              : debug,
	identify           : identify,
	group              : group,
	page               : page,
	track              : track
};

export { Analytics };
